<template>
  <div class="timesheets-list layout">
    <remove-timesheet-modal ref="removeTimesheetModal" @remove="applyFilters()"></remove-timesheet-modal>

    <column-filter-modal ref="tableFilterModal" @apply="setTableFilter"></column-filter-modal>

    <columns-modal
      ref="columnsModal"
      :columns="columnsList"
      :default-selected="defaultColumns"
      @apply="saveUserColumns"
    />

    <div class="layout-header">
      <div class="layout-title">Табели учёта рабочего времени</div>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <c-button2
            size="small"
            :icon="{
              icon: 'bookmark',
              activeColor: 'white',
            }"
            :tooltip="{
              text: 'Добавить в закладки',
              activeText: 'Убрать из закладок',
            }"
            active-color="blue"
            :active="bookmark"
            @click="bookmark = !bookmark"
          />
        </div>
        <div v-if="canEdit" class="layout-header-action">
          <c-button2
            :responsive="true"
            size="small"
            label="Добавить табель"
            color="primary"
            :icon="{ icon: 'plus', color: 'white' }"
            @click="addTimesheet"
          />
        </div>
      </div>
    </div>

    <div class="timesheets-list__content">
      <template v-if="!inited">
        <c-loader :active="true" :height="250" label="Данные загружаются"></c-loader>
      </template>

      <template v-else>
        <div class="timesheets-list-filters">
          <month-period-select
            v-model="filters.period"
            class="timesheets-list-filters__item timesheets-list-filters__period"
            label="Период"
            placeholder="Весь период"
            :clearable="true"
          ></month-period-select>
          <c-input2
            v-model="filters.filials"
            class="timesheets-list-filters__item timesheets-list-filters__filials"
            type="select"
            label="Филиал"
            :checkbox="true"
            :checkbox-value="filters.filialsExcept"
            :checkbox-type="'icon'"
            :checkbox-tooltip="checkboxTooltip"
            :multiple="true"
            :searchable="true"
            :clearable="true"
            placeholder="Все"
            :items="collections.filials"
            @checkboxChange="filters.filialsExcept = $event"
          />
          <div class="timesheets-list-filters__actions timesheets-list-filters__item">
            <c-button class="timesheets-list-filters__reset" size="large" label="Сбросить" @click="resetFilters" />
            <c-button
              class="timesheets-list-filters__apply"
              label="Применить"
              color="blue"
              size="large"
              @click="applyFilters"
            />
          </div>
        </div>

        <template v-if="empty">
          <div class="timesheets-list__empty">Нет табелей</div>
        </template>

        <template v-else>
          <div class="timesheets-list__table-actions">
            <c-button
              class="timesheets-list__reset-table-filters"
              responsive
              :disabled="!hasTableFilters"
              label="Сбросить фильтры столбцов"
              :icon="{ color: 'pink', icon: 'filter-drop' }"
              type="with-icon"
              @click="resetTableFilters"
            />

            <c-button
              class="timesheets-list__config-columns"
              responsive
              label="Настроить столбцы"
              icon="cog"
              type="with-icon"
              @click="configureColumns"
            />
          </div>

          <div class="timesheets-list__table-wrapper">
            <div class="lg">
              <scrolled-table @nextPage="nextPage">
                <table class="h-table timesheets-list__table">
                  <thead>
                    <tr>
                      <th v-for="column in columns" :key="column.code" :data-column="column.code">
                        <div class="h-table__header">
                          <div
                            class="h-table__col-title"
                            :class="{
                              active: sortBy === column.code,
                              sorted: sortableColumns.includes(column.code),
                            }"
                            @click="sort(column.code)"
                          >
                            {{ column.shortName }}{{ column.unit ? ", " + column.unit : "" }}
                          </div>
                          <div class="h-table__actions">
                            <c-button icon="info" :tooltip="column.fullName" size="small" no-border />
                            <template v-if="tableFilters[column.code]">
                              <c-button
                                :id="`timesheets-list-table-filter--` + column.unique"
                                :icon="{
                                  icon: 'filter-column',
                                  activeColor: 'blue',
                                }"
                                no-border
                                size="small"
                                :active="!!tableFilters[column.code].value"
                                @click="configTableFilter(column)"
                              />
                              <b-popover
                                id="h-table-filter-popover"
                                placement="top"
                                triggers="hover blur"
                                :target="`timesheets-list-table-filter--` + column.unique"
                              >
                                {{ tableFilters[column.code].value ? "Изменить фильтр" : "Применить фильтр" }}
                              </b-popover>
                            </template>
                          </div>
                        </div>
                      </th>
                      <th data-column="actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in list" :key="item.id" class="timesheet-item">
                      <td
                        v-for="column in columns"
                        :key="column.code"
                        class="h-table__cell"
                        :data-column="column.code"
                        :class="{
                          'h-table__cell--negative': column.code === 'penalties' && item.fines > 0,
                          'h-table__cell--positive': column.code === 'bonuses' && item.bonuses > 0,
                        }"
                      >
                        <template v-if="column.code === 'created'">{{ item.created }}</template>
                        <template v-if="column.code === 'filial'">{{ item.filial }}</template>
                        <template v-if="column.code === 'period'">{{ item.period }}</template>
                        <template v-if="column.code === 'author'">{{ item.author }}</template>
                        <template v-if="column.code === 'penalties'">{{ item.fines.toLocaleString() }}</template>
                        <template v-if="column.code === 'bonuses'">{{ item.bonuses.toLocaleString() }}</template>
                        <template v-if="column.code === 'paid'">{{ item.paid.toLocaleString() }}</template>
                      </td>
                      <td data-column="actions">
                        <div class="timesheet-item__actions">
                          <c-button2
                            v-b-popover.hover.blur.top="'Табель ' + item.filial + ', ' + item.period"
                            class="timesheet-item__edit"
                            size="iconic"
                            icon="edit"
                            @click="toTimesheet(item)"
                          />
                          <c-button2
                            v-if="canEdit"
                            v-b-popover.hover.blur.top="'Удалить'"
                            class="timesheet-item__remove"
                            size="iconic"
                            :icon="{ icon: 'trash', color: 'secondary' }"
                            @click="removeTimesheet(item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </scrolled-table>
            </div>
            <div class="sm">
              <horizontal-table @nextPage="nextPage">
                <table class="h-table">
                  <tbody>
                    <tr v-for="column in columns" :key="column.code" :data-column="column.code">
                      <th>
                        <div class="h-table__title">
                          <div
                            class="h-table__col-title"
                            :class="{
                              active: sortBy === column.code,
                              sorted: sortableColumns.includes(column.code),
                            }"
                            @click="sort(column.code)"
                          >
                            {{ column.shortName }}{{ column.unit ? ", " + column.unit : "" }}
                          </div>
                          <div class="h-table__actions">
                            <template v-if="tableFilters[column.code]">
                              <c-button
                                :icon="{
                                  icon: 'filter-column',
                                  activeColor: 'blue',
                                }"
                                no-border
                                size="small"
                                :active="!!tableFilters[column.code].value"
                                @click="configTableFilter(column)"
                              />
                            </template>
                          </div>
                        </div>
                      </th>
                      <td
                        v-for="item in list"
                        :key="item.id"
                        class="h-table__cell"
                        :data-column="column.code"
                        :class="{
                          'h-table__cell--negative': column.code === 'penalties' && item.fines > 0,
                          'h-table__cell--positive': column.code === 'bonuses' && item.bonuses > 0,
                        }"
                      >
                        <template v-if="column.code === 'created'">{{ item.created }}</template>
                        <template v-if="column.code === 'filial'">{{ item.filial }}</template>
                        <template v-if="column.code === 'period'">{{ item.period }}</template>
                        <template v-if="column.code === 'author'">{{ item.author }}</template>
                        <template v-if="column.code === 'penalties'">{{ item.fines.toLocaleString() }}</template>
                        <template v-if="column.code === 'bonuses'">{{ item.bonuses.toLocaleString() }}</template>
                        <template v-if="column.code === 'paid'">{{ item.paid.toLocaleString() }}</template>
                      </td>
                    </tr>
                    <tr>
                      <th data-column="actions"></th>
                      <td v-for="item in list" :key="item.id" data-column="actions">
                        <div class="timesheet-item__actions">
                          <c-button2
                            class="timesheet-item__edit"
                            size="iconic"
                            icon="edit"
                            @click="toTimesheet(item)"
                          />
                          <c-button2
                            v-if="canEdit"
                            class="timesheet-item__remove"
                            size="iconic"
                            :icon="{ icon: 'trash', color: 'secondary' }"
                            @click="removeTimesheet(item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </horizontal-table>
            </div>
            <div v-if="!loading && !list.length" class="timesheets-list__not-found">Ничего не найдено</div>
            <c-loader
              v-if="loading"
              class="timesheets-list__loader"
              :active="loading"
              :height="250"
              label="Данные загружаются"
            ></c-loader>
          </div>
        </template>
      </template>
    </div>

    <transition name="fade">
      <div v-if="!loading" class="timesheets-list__summary">
        Показано <b>{{ list.length }}</b> из <b>{{ totalCount }}</b>
      </div>
    </transition>

    <top-button root=".layout"></top-button>
  </div>
</template>

<script>
import moment from "moment";
import CButton2 from "@/components/controls2/Button";
import CButton from "@/components/controls/button";
import CInput2 from "@/components/controls2/Input";
import TopButton from "@/components/top-button";
import CLoader from "@/components/loader";
import ScrolledTable from "@/components/scrolled-table";
import HorizontalTable from "@/components/horizontal-table";
import ColumnFilterModal from "@/components/modals/column-filter-modal";
import ColumnsModal from "@/components/modals/table-columns/modal/";
import MonthPeriodSelect from "@/components/month-period-select";
import RemoveTimesheetModal from "../components/remove-timesheet-modal";
import { Column } from "@/entities/structures/column";

import { Timesheet } from "../entities/timesheet";

const checkboxTooltip = {
  disabledText: "Фильтр по всем значениям в справочнике, кроме выбранных",
  activeText: "Выключить фильтр по всем значениям в справочнике, кроме выбранных",
  notActiveText: "Включить фильтр по всем значениям в справочнике, кроме выбранных",
};

export default {
  components: {
    CButton2,
    CButton,
    TopButton,
    CLoader,
    CInput2,
    ScrolledTable,
    HorizontalTable,
    RemoveTimesheetModal,
    MonthPeriodSelect,
    ColumnsModal,
    ColumnFilterModal,
  },

  data() {
    return {
      bookmark: false,

      canEdit: false,

      inited: false,
      loading: false,
      empty: false,

      collections: {
        filials: [],
      },

      columnsList: [],
      visibleColumns: [],
      sortableColumns: ["created", "filial", "period", "author"],

      checkboxTooltip,

      page: 1,

      sortBy: "created",
      desc: true,
      filters: {
        period: "",
        filials: [],
        filialsExcept: false,
      },
      tableFilters: {
        created: {
          type: "date",
          value: null,
        },
        filial: {
          type: "list",
          value: null,
        },
        author: {
          type: "string",
          value: null,
        },
      },

      list: [],
      lastRequestEmpty: false,
      totalCount: 0,
    };
  },

  computed: {
    defaultColumns() {
      return this.columnsList.map((c) => c.code);
    },
    columns() {
      return this.columnsList.filter((c) => this.visibleColumns.includes(c.code));
    },
    hasTableFilters() {
      return Object.values(this.tableFilters).some((f) => f.value);
    },
  },

  created() {
    this.getEditPermission().then((canEdit) => {
      this.canEdit = canEdit;
    });

    this.getFilials().then((items) => {
      this.collections.filials = items;
    });

    Promise.all([
      this.getColumnsSet().then((res) => {
        this.columnsList = res;
      }),
      this.getStartColumnsSet().then((res) => {
        this.visibleColumns = res;
      }),
      this.getList({}).then((res) => {
        if (!res.totalCount) this.empty = true;
      }),
    ]).then(() => {
      this.inited = true;
      if (this.empty) return;
      this.setDefaultFilters();
      this.applyFilters();
    });
  },

  methods: {
    addTimesheet() {
      this.$router.push({
        name: "create-timesheet",
      });
    },

    removeTimesheet(timesheet) {
      this.$refs.removeTimesheetModal.open(timesheet);
    },

    toTimesheet(timesheet) {
      this.$router.push({
        name: "edit-timesheet",
        params: {
          id: timesheet.id,
        },
      });
    },

    getEditPermission() {
      const permissions = ["controlTimesheet", "controlTimesheetAll"];
      return this.$store.dispatch("getUserPermissions").then(() => {
        const userPermissions = this.$store.getters.getUserPermissions;
        return userPermissions.some((permission) => permissions.includes(permission));
      });
    },

    getColumnsSet() {
      return this.$store.dispatch("timesheets/getColumnsList").then((columns) => {
        columns.sort((a, b) => a.sort - b.sort);
        return columns.map((c) => new Column(c));
      });
    },
    getStartColumnsSet() {
      return this.$store.dispatch("timesheets/getUserColumnsList").then((res) => res.columns);
    },

    getFilials() {
      return this.$store.dispatch("filters/loadFilials", {
        all: "viewTimesheetAll",
        own: "viewTimesheet",
      });
    },

    setDefaultFilters() {},

    saveFilters() {},

    getTableFilters() {
      const params = {};

      Object.entries(this.tableFilters).forEach(([key, filter]) => {
        if (!filter.value) return;

        if (key === "author") {
          if (filter.value.searchType.toString() === "1") {
            params.createdBy = filter.value.value;
          } else {
            params.createdBySearch = filter.value.value;
          }
        } else if (key === "filial") {
          params.filial_search_ids = filter.value.values;
          params.filial_search_ids_except = filter.value.searchType.toString() === "2" ? 1 : 0;
        } else if (key === "created") {
          if (filter.value.searchType === "date") {
            params.created_at = moment(filter.value.value, "DD.MM.YYYY").format("YYYY-MM-DD");
          } else if (filter.value.searchType === "after") {
            params.created_at_from = moment(filter.value.value, "DD.MM.YYYY").format("YYYY-MM-DD");
          } else if (filter.value.searchType === "before") {
            params.created_at_to = moment(filter.value.value, "DD.MM.YYYY").format("YYYY-MM-DD");
          } else if (filter.value.searchType === "range") {
            params.created_at_from = moment(filter.value.from, "DD.MM.YYYY").format("YYYY-MM-DD");
            params.created_at_to = moment(filter.value.to, "DD.MM.YYYY").format("YYYY-MM-DD");
          }
        }
      });

      return params;
    },

    getParams() {
      const params = {
        sortBy: this.sortBy,
        desc: this.desc,
        page: this.page,
      };

      if (this.filters.filials.length) {
        params.filials = this.filters.filials.map((f) => f.value);
        params.filialsExcept = this.filters.filialsExcept;
      }

      if (this.filters.period) {
        params.periodFrom = this.filters.period.from;
        params.periodTo = this.filters.period.to;
      }

      params.tableFilters = this.getTableFilters();

      return params;
    },

    getList(params) {
      return this.$store.dispatch("timesheets/getList", params);
    },

    load() {
      this.loading = true;

      const params = this.getParams();
      this.saveFilters(params);

      this.getList(params).then((res) => {
        this.list = [...this.list, ...(res.items || []).map((i) => new Timesheet(i))];
        this.totalCount = res.totalCount;

        this.lastRequestEmpty = this.list.length >= this.totalCount;

        this.loading = false;
      });
    },

    applyFilters() {
      this.loading = false;
      this.lastRequestEmpty = false;
      this.page = 1;
      this.list = [];

      this.load();
    },

    resetFilters() {
      this.filters.filials = [];
      this.filters.filialsExcept = false;
      this.filters.period = null;
      this.applyFilters();
    },

    sort(sortBy) {
      if (!this.sortableColumns.includes(sortBy)) return;
      if (this.sortBy === sortBy) {
        this.desc = !this.desc;
      } else {
        this.sortBy = sortBy;
        this.desc = false;
      }
      this.applyFilters();
    },

    // eslint-disable-next-line consistent-return
    nextPage() {
      if (this.loading) return false;
      if (this.lastRequestEmpty) return false;
      this.page += 1;
      this.load();
    },

    configTableFilter(column) {
      const filter = this.tableFilters[column.code];
      if (!filter) return;

      this.$refs.tableFilterModal.open({
        name: column.code,
        type: filter.type,
        value: filter.value,
        getList: () => this.getReferenceValues(column.code),
      });
    },

    getReferenceValues(columnName) {
      if (columnName === "filial") return this.getFilials();
      return Promise.resolve([]);
    },

    setTableFilter({ name, value }) {
      this.tableFilters = {
        ...this.tableFilters,
        [name]: {
          type: this.tableFilters[name].type,
          value,
        },
      };
      this.applyFilters();
    },

    resetTableFilters() {
      const filters = {};
      Object.keys(this.tableFilters).forEach((key) => {
        filters[key] = {
          type: this.tableFilters[key].type,
          value: null,
        };
      });
      this.tableFilters = filters;
      this.applyFilters();
    },

    configureColumns() {
      this.$refs.columnsModal.open(this.visibleColumns);
    },

    saveUserColumns(data) {
      this.visibleColumns = data;
      this.$store.dispatch("timesheets/saveUserColumnsList", { columns: data });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.timesheets-list {
  .sm {
    display: none;
  }

  .layout-header {
    height: auto;
  }

  &__table {
    td {
      &[data-column="period"],
      &[data-column="penalties"],
      &[data-column="bonuses"],
      &[data-column="paid"] {
        text-align: right;
      }
    }

    [data-column="actions"] {
      width: 70px;
      max-width: 70px;
    }
  }

  &__table-wrapper {
    position: relative;
  }

  &__loader {
    margin-top: -14px;
  }
  &__summary {
    position: fixed;
    right: 50px;
    bottom: 10px;
    background: #ffffff;
    box-shadow: 0px 2px 10px rgba(0, 15, 38, 0.25);
    border-radius: 50px;
    z-index: 5;
    height: 30px;
    line-height: 30px;
    font-size: 12px;
    padding: 0px 20px;

    b {
      font-weight: 500;
    }
  }

  .timesheet-item {
    &__actions {
      display: flex;
    }
    &__remove {
      margin-top: -2px;
      margin-left: 10px;
    }
    &__edit {
      margin-left: 2px;
    }
  }

  &__not-found {
    font-size: 15px;
    color: $color-label;
    border: 1px solid #dfe4ef;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -14px;
  }

  &__empty {
    font-size: 15px;
    color: $color-label;
    border: 1px solid #dfe4ef;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .h-table {
    &__actions {
      margin-left: -4px;
      margin-top: 8px;
    }
    &__col-header {
      &:not(.sorted) {
        cursor: default;
      }
    }
  }
  &-filters {
    margin-bottom: 5px;
    display: flex;
    margin-left: -15px;
    margin-right: -15px;

    &__item {
      margin-bottom: 25px;
      padding-left: 15px;
      padding-right: 15px;
    }

    &__period {
      width: 267px;
      flex-shrink: 0;
    }

    &__filials {
      min-width: 366px;
    }

    &__actions {
      display: flex;
      flex-shrink: 0;
    }
    &__reset {
      margin-right: 10px;
    }
  }
  &__table-actions {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-end;
  }
  &__reset-table-filters {
    margin-right: 10px;
  }

  @include mobileAndTablet {
    padding-bottom: 100px;

    &-filters {
      flex-wrap: wrap;

      &__actions {
        width: 100%;
        justify-content: flex-end;
      }

      &__filials {
        flex-grow: 1;
      }
    }
  }
  @include onlyMobile {
    padding-top: 25px;

    .layout-header {
      margin-bottom: 20px;
      &-actions {
        margin: 0 -5px;
      }
      &-action {
        margin: 0 5px;
      }
    }

    &-filters {
      flex-direction: column;
      margin-bottom: 0;

      &__period {
        width: 100%;
      }
      &__filials {
        width: 100%;
        min-width: 0;
      }

      &__actions {
        width: calc(100% + 20px);
        margin-left: -7.5px;
        margin-right: -7.5px;

        & /deep/ {
          .c-button {
            margin: 0 7.5px;
            flex-grow: 1;
            flex-basis: 50%;

            &__wrapper {
              width: 100%;
              justify-content: center;
            }
          }
        }
      }
    }
    &__loader {
      position: absolute;
      top: 0;
      left: -20px;
      right: -20px;
      bottom: 0;
      min-height: 250px;
      height: 100% !important;
      margin-top: 0;
    }
    .lg {
      display: none;
    }
    .sm {
      display: block;
      margin-left: -20px;
      margin-right: -20px;

      .h-table__title {
        display: flex;
        width: 100%;
        align-items: center;
      }
      .h-table__actions {
        margin-top: 0;
        margin-left: auto;
        padding-left: 8px;
      }

      th {
        width: 110px;
      }

      td {
        width: calc(100vw - 110px);
      }
    }
  }
}
</style>
